import React, { useEffect, useState } from 'react';
import { Badge, Group, Loader, Modal, Text, Timeline, Center } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { CotizadorModelo, type IHistorialEstadoCotizacion } from '../../../modelos/CotizadorNBS';
import { colorEstadoCotizacion, nombreEstadoCotizacion } from '../../../modelos/CotizadorNBS/entidades/EstadoCotizacionNBS';
import { useAvisoErrorConsulta } from '../../configuracionCotizadorNbs/controladorVista/useAvisoErrorConsulta';

interface HistorialEstadosCVProps {
    cotizacionId: string;
    abierto: boolean;
    alCerrar: () => void;
}

/** Quién cambió el estado de la cotización y cuándo (lo registra el back en cada guardado). */
export const HistorialEstadosCV: React.FC<HistorialEstadosCVProps> = ({ cotizacionId, abierto, alCerrar }) => {
    const [historial, setHistorial] = useState<IHistorialEstadoCotizacion[] | null>(null);
    const avisarErrorConsulta = useAvisoErrorConsulta();

    useEffect(() => {
        if (!abierto) return;
        setHistorial(null);
        new CotizadorModelo().obtenerHistorialEstados(cotizacionId)
            .then(respuesta => setHistorial(respuesta.Data ?? []))
            .catch(error => {
                avisarErrorConsulta(error, 'el historial de estados');
                setHistorial([]);
            });
    }, [abierto, cotizacionId]);

    return (
        <Modal opened={abierto} onClose={alCerrar} title={<Text fw={600}>Historial de estados</Text>} centered>
            {historial === null ? (
                <Center py="md"><Loader size="sm" /></Center>
            ) : historial.length === 0 ? (
                <Text size="sm" c="dimmed">No hay cambios de estado registrados.</Text>
            ) : (
                // Un punto relleno del color de cada estado (mismos colores que la etiqueta de estado).
                <Timeline active={historial.length} bulletSize={20} lineWidth={2}>
                    {historial.map((cambio, indice) => (
                        <Timeline.Item
                            key={cambio.Id}
                            color={colorEstadoCotizacion(cambio.EstadoNuevo)}
                            // Punto relleno (fondo y borde) con el color del estado.
                            // El primero (más reciente) es el estado actual: se marca con un check blanco.
                            bullet={indice === 0 ? <IconCheck size={12} stroke={3} color="#ffffff" /> : undefined}
                            styles={{ itemBullet: { backgroundColor: colorEstadoCotizacion(cambio.EstadoNuevo), borderColor: colorEstadoCotizacion(cambio.EstadoNuevo) } }}
                            title={
                                <Group gap={6}>
                                    <Text size="sm" fw={500}>
                                        {cambio.EstadoAnterior == null
                                            ? `Creada en ${nombreEstadoCotizacion(cambio.EstadoNuevo)}`
                                            : `${nombreEstadoCotizacion(cambio.EstadoAnterior)} → ${nombreEstadoCotizacion(cambio.EstadoNuevo)}`}
                                    </Text>
                                    {indice === 0 && (
                                        <Badge color="primary" variant="light" radius="xl" size="sm" style={{ textTransform: 'none' }}>Actual</Badge>
                                    )}
                                </Group>
                            }
                        >
                            <Text size="xs" c="dimmed">
                                {cambio.Usuario} · {new Date(cambio.Fecha.endsWith('Z') ? cambio.Fecha : `${cambio.Fecha}Z`).toLocaleString('es-CO')}
                            </Text>
                        </Timeline.Item>
                    ))}
                </Timeline>
            )}
        </Modal>
    );
};
